/**
 * BscLessons — Registro correzioni manuali all'estrazione LLM (Frabemar).
 *
 * Replica il meccanismo Casadei (audit_log.lesson_learned):
 *  - Casadei: lesson_learned descrive quale edificio/impianto era sbagliato → corretto.
 *  - BSC: lesson_learned descrive quale campo estratto era sbagliato → corretto (es. Esportatore, Totale invoice).
 *
 * Differenza chiave rispetto a Casadei:
 *  - Casadei: le correzioni generalizzano a NUOVI documenti tramite learned_rules
 *    (keyword-based rules iniettate nel prompt LLM) e learned_associations
 *    (email→entità, per mittenti ricorrenti).
 *  - BSC: ogni documento è unico (BL number diverso, aziende diverse). Le correzioni
 *    sono per-pratica: si applicano automaticamente al Riprocessa della STESSA pratica,
 *    ma non influenzano l'estrazione di pratiche future.
 *
 * Questa pagina mostra l'audit trail delle correzioni manuali (come Casadei
 * mostra "Configurazione → Apprendimenti").
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchLessons, deleteLesson } from "../../api/client";
import type { AuditEntry } from "../../api/types";
import { useToast } from "../../context/ToastContext";
import { ConfirmModal } from "../../components/ConfirmModal";

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("it-IT", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

export default function BscLessons() {
  const [lessons, setLessons] = useState<AuditEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const navigate = useNavigate();
  const { addToast } = useToast();

  const load = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await fetchLessons(1, 100);
      setLessons(data.items ?? []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Errore caricamento correzioni");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      await deleteLesson(id);
      setLessons((prev) => prev.filter((l) => l.id !== id));
      addToast("success", "Correzione rimossa");
    } catch (e: unknown) {
      addToast("error", `Errore durante la rimozione: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setDeletingId(null);
      setConfirmDeleteId(null);
    }
  };

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", padding: "var(--space-6)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)", marginBottom: "var(--space-6)" }}>
        <span className="material-symbols-outlined" style={{ fontSize: 28, color: "var(--indaco-brand-primary)" }}>school</span>
        <h1 className="page-title" style={{ margin: 0 }}>Correzioni BSC</h1>
      </div>

      <div className="card" style={{ marginBottom: "var(--space-4)", padding: "var(--space-4)", background: "var(--indaco-info-light)" }}>
        <p style={{ margin: 0, fontSize: "var(--font-size-sm)" }}>
          <strong>Registro correzioni — equivalente BSC delle "Apprendimenti" Casadei.</strong>{" "}
          Quando approvi una pratica BSC modificando campi estratti dall'AI, la correzione viene registrata.
          Le correzioni su nomi di entità (<em>Importatore</em>, <em>Esportatore</em> e relativi paesi/città)
          si generalizzano automaticamente a <strong>pratiche future e al Riprocessa</strong>:{" "}
          i partner commerciali ricorrenti (es. SOBRAGUI SA, Red Bull GmbH) vengono normalizzati senza intervento manuale.
          Le correzioni su campi non-entità (BL#, totale, ecc.) si applicano solo alla stessa pratica.
        </p>
      </div>

      {isLoading && (
        <div className="loading-center">
          <div className="spinner" />
        </div>
      )}

      {error && (
        <div className="alert alert-danger">
          {error}
        </div>
      )}

      {!isLoading && !error && lessons.length === 0 && (
        <div className="card" style={{ padding: "var(--space-8)", textAlign: "center" }}>
          <span className="material-symbols-outlined" style={{ fontSize: 48, color: "var(--indaco-gray-300)", display: "block", marginBottom: "var(--space-3)" }}>school</span>
          <p className="text-muted">Nessuna correzione registrata.</p>
          <p className="text-sm text-muted">Le correzioni appaiono qui quando approvi una pratica BSC modificando i campi estratti dall'AI.</p>
        </div>
      )}

      {!isLoading && lessons.length > 0 && (
        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Data</th>
                  <th>BL / Pratica</th>
                  <th>Correzione</th>
                  <th>Operatore</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {lessons.map((l) => {
                  // BSC lessons store practice_id in details.bsc_practice_id
                  // (audit_log.request_id is NULL for BSC — different FK table)
                  const practiceId = (l.details?.bsc_practice_id as string | undefined) ?? l.request_id;
                  const blNumber = (l.details?.bl_number as string | undefined) ?? null;
                  const fieldsArr = (l.details?.fields_corrected as string[] | undefined) ?? [];
                  return (
                    <tr key={l.id}>
                      <td style={{ whiteSpace: "nowrap" }}>
                        {formatDate(l.created_at)}
                      </td>
                      <td>
                        {blNumber && (
                          <div style={{ fontSize: "var(--font-size-xs)", fontWeight: "var(--font-weight-semibold)", marginBottom: 2 }}>
                            {blNumber}
                          </div>
                        )}
                        {practiceId && (
                          <button
                            className="btn btn-outline"
                            style={{ padding: "2px 8px", fontSize: "var(--font-size-xs)" }}
                            onClick={(e) => { e.stopPropagation(); navigate(`/bsc/${practiceId}`); }}
                          >
                            {practiceId.slice(0, 8)}…
                          </button>
                        )}
                      </td>
                      <td>
                        <span style={{ color: "var(--indaco-success)" }}>{l.lesson_learned}</span>
                        {fieldsArr.length > 0 && (
                          <div style={{ marginTop: 4, display: "flex", flexWrap: "wrap", gap: 4 }}>
                            {fieldsArr.map((f) => {
                              const isEntity = ["importer_name","importer_country","importer_city","exporter_name","exporter_country","exporter_city"].includes(f);
                              return (
                                <span
                                  key={f}
                                  className={isEntity ? "badge badge-success" : "badge badge-neutral"}
                                  style={{ fontSize: "var(--font-size-xs)" }}
                                  title={isEntity ? "Generalizza a pratiche future" : "Solo questa pratica"}
                                >
                                  {f}{isEntity ? " ↻" : ""}
                                </span>
                              );
                            })}
                          </div>
                        )}
                      </td>
                      <td>
                        {l.operator_id?.slice(0, 16) ?? "—"}
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <button
                          className="btn btn-outline"
                          style={{ padding: "2px 6px", fontSize: 12, color: "var(--indaco-danger)" }}
                          disabled={deletingId === l.id}
                          onClick={() => setConfirmDeleteId(l.id)}
                          title="Rimuovi correzione"
                        >
                          {deletingId === l.id ? "…" : "✕"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ padding: "var(--space-3) var(--space-4)", borderTop: "1px solid var(--indaco-gray-100)", color: "var(--indaco-gray-500)", fontSize: "var(--font-size-xs)" }}>
            {lessons.length} correzione{lessons.length !== 1 ? "i" : ""} registrata{lessons.length !== 1 ? "e" : ""}
          </div>
        </div>
      )}

      {confirmDeleteId && (
        <ConfirmModal
          title="Rimuovi correzione"
          message="Vuoi davvero rimuovere questa correzione? L'operazione non è reversibile."
          confirmLabel="Rimuovi"
          danger
          loading={deletingId === confirmDeleteId}
          onConfirm={() => handleDelete(confirmDeleteId)}
          onCancel={() => setConfirmDeleteId(null)}
        />
      )}
    </div>
  );
}
